import { Link } from 'react-router-dom';
import { formatMoney, type JobStatusValue } from '@teentack/shared';
import { useMyJobs } from '@/features/jobs/use-jobs';
import { useJobApplications } from '@/features/applications/use-applications';
import { PosterShell } from '@/features/poster/PosterShell';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  BadgeCheckIcon,
  BriefcaseIcon,
  UsersIcon,
  WalletIcon,
} from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';

const STATUS_TONES: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  LIVE: 'safe',
  IN_PROGRESS: 'brand',
  COMPLETED: 'accent',
  CLOSED: 'neutral',
  FLAGGED: 'sun',
};

function label(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');
}

/**
 * The job poster's home. [Job Poster step 4 onward]
 *
 * Their listings, how each one is doing, and the one action that matters when
 * they have none: post something.
 */
export function MyJobsPage() {
  const jobs = useMyJobs({ pageSize: 50 });
  const applications = useJobApplications({ pageSize: 100 });

  /** Applications per job, so each card shows its own count. */
  const applicationsByJob = new Map<string, number>();
  for (const application of applications.data?.items ?? []) {
    applicationsByJob.set(application.job.id, (applicationsByJob.get(application.job.id) ?? 0) + 1);
  }

  return (
    <PosterShell title="My jobs" description="Everything you have posted, and who has applied.">
      {jobs.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      )}

      {jobs.isError && (
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <p className="text-sm text-slate-500">
            {presentApiError(jobs.error, 'Could not load your jobs').message}
          </p>
          <Button className="mt-5" onClick={() => void jobs.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {jobs.data?.items.length === 0 && (
        <EmptyState
          icon={<BriefcaseIcon className="size-6" />}
          title="No jobs yet"
          action={
            <Link
              to="/jobs/new"
              className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-700"
            >
              Post a job
            </Link>
          }
        >
          Post your first job and verified, guardian-approved teens can start applying.
        </EmptyState>
      )}

      <ul className="space-y-4">
        {jobs.data?.items.map((job) => {
          const applied = applicationsByJob.get(job.id) ?? 0;
          return (
            <li
              key={job.id}
              className="rounded-2xl border border-line bg-white p-5 shadow-soft transition hover:border-brand-200 sm:p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <Link
                    to={`/jobs/${job.id}`}
                    className="text-base font-bold text-ink hover:underline"
                  >
                    {job.title}
                  </Link>
                  <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{job.category}</span>
                    <span className="inline-flex items-center gap-1 font-bold text-ink">
                      <WalletIcon className="size-3.5 text-safe-600" />
                      {formatMoney(job.pay.amountMinor, job.pay.currency)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <UsersIcon className="size-3.5 text-slate-400" />
                      {job.teensAssigned}/{job.teensNeeded} filled
                    </span>
                  </p>
                </div>
                <Badge tone={STATUS_TONES[job.status as JobStatusValue] ?? 'neutral'}>
                  {label(job.status)}
                </Badge>
              </div>

              {/* A filled job that everyone has accepted is a milestone, and a
                  count alone does not read as one. [Job Poster step 20] */}
              {job.teensNeeded > 0 && job.teensAssigned >= job.teensNeeded && (
                <p className="mt-4 flex items-center gap-2 rounded-xl border border-safe-200 bg-safe-50 px-4 py-3 text-sm font-semibold text-safe-800">
                  <BadgeCheckIcon className="size-4 shrink-0" />
                  Team confirmed — all {job.teensNeeded}{' '}
                  {job.teensNeeded === 1 ? 'place is' : 'places are'} filled.
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                <p className="text-sm text-slate-500">
                  {applied === 0
                    ? job.status === 'DRAFT'
                      ? 'Not published yet'
                      : 'No applications yet'
                    : `${applied} application${applied === 1 ? '' : 's'}`}
                </p>
                <div className="flex gap-2">
                  <Link
                    to={`/jobs/${job.id}`}
                    className="rounded-xl border border-line bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:border-brand-300 hover:bg-brand-50"
                  >
                    View
                  </Link>
                  {applied > 0 && (
                    <Link
                      to={`/applicants?jobId=${job.id}`}
                      className="rounded-xl bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-brand-700"
                    >
                      Review applicants
                    </Link>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </PosterShell>
  );
}
